import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { format } from 'vscode/vscode/vs/base/common/strings';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { IAccessibilityService } from 'vscode/vscode/vs/platform/accessibility/common/accessibility.service';
import { ICommandService } from 'vscode/vscode/vs/platform/commands/common/commands.service';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { IKeybindingService } from 'vscode/vscode/vs/platform/keybinding/common/keybinding.service';
import { accessibleViewIsShown, accessibleViewCurrentProviderId } from 'vscode/vscode/vs/workbench/contrib/accessibility/browser/accessibilityConfiguration';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';

const _moduleId = "vs/workbench/contrib/terminalContrib/accessibility/browser/terminalAccessibilityHelp";
let TerminalAccessibilityHelpProvider = class TerminalAccessibilityHelpProvider extends Disposable {
    onClose() {
        const expr = ( (ContextKeyExpr.and(accessibleViewIsShown,  (ContextKeyExpr.equals(
            accessibleViewCurrentProviderId.key,
            "terminal-help"
        )))));
        if (expr?.evaluate(this._contextKeyService.getContext(null))) {
            this._commandService.executeCommand("workbench.action.terminal.focusAccessibleBuffer" );
        }
        else {
            this._instance.focus();
        }
        this.dispose();
    }
    constructor(_instance, _xterm, _instantiationService, _keybindingService, _contextKeyService, _commandService, _accessibilityService, _configurationService) {
        super();
        this._instance = _instance;
        this._keybindingService = _keybindingService;
        this._contextKeyService = _contextKeyService;
        this._commandService = _commandService;
        this._accessibilityService = _accessibilityService;
        this._configurationService = _configurationService;
        this.id = "terminal-help" ;
        this._hasShellIntegration = false;
        this.options = {
            type: "help" ,
            readMoreUrl: 'https://code.visualstudio.com/docs/editor/accessibility#_terminal-accessibility'
        };
        this.verbositySettingKey = "accessibility.verbosity.terminal" ;
        this._hasShellIntegration = _xterm.shellIntegration.status === 2 ;
    }
    _descriptionForCommand(commandId, msg, noKbMsg) {
        if (commandId === "workbench.action.terminal.runRecentCommand" ) {
            const kb = this._keybindingService.lookupKeybindings(commandId);
            const isScreenReaderOptimized = this._accessibilityService.isScreenReaderOptimized();
            if (isScreenReaderOptimized && kb[1]) {
                format(msg, kb[1].getAriaLabel());
            }
            else if (kb[0]) {
                format(msg, kb[0].getAriaLabel());
            }
            else {
                return format(noKbMsg, commandId);
            }
        }
        const kb = this._keybindingService.lookupKeybinding(commandId, this._contextKeyService)?.getAriaLabel();
        return !kb ? format(noKbMsg, commandId) : format(msg, kb);
    }
    provideContent() {
        const content = [];
        content.push(this._descriptionForCommand("workbench.action.terminal.focusAccessibleBuffer" , ( localizeWithPath(
            _moduleId,
            0,
            'The Focus Accessible Terminal View ({0}) command enables screen readers to read terminal contents.'
        )), ( localizeWithPath(
            _moduleId,
            1,
            'The Focus Terminal Accessible View command enables screen readers to read terminal contents and is currently not triggerable by a keybinding.'
        ))));
        content.push(( localizeWithPath(
            _moduleId,
            2,
            'Customize the behavior of the cursor when toggling between the terminal and accessible view with `terminal.integrated.accessibleViewPreserveCursorPosition.`'
        )));
        if (!this._configurationService.getValue("terminal.integrated.accessibleViewFocusOnCommandExecution" )) {
            content.push(( localizeWithPath(
                _moduleId,
                3,
                'Enable `terminal.integrated.accessibleViewFocusOnCommandExecution` to automatically focus the terminal accessible view when a command is executed in the terminal.'
            )));
        }
        if (this._instance.shellType === "cmd" ) {
            content.push(( localizeWithPath(
                _moduleId,
                4,
                "Consider using powershell instead of command prompt for an improved experience"
            )));
        }
        if (this._hasShellIntegration) {
            const shellIntegrationCommandList = [];
            shellIntegrationCommandList.push(( localizeWithPath(
                _moduleId,
                5,
                "The terminal has a feature called shell integration that offers an enhanced experience and provides useful commands for screen readers such as:"
            )));
            shellIntegrationCommandList.push('- ' + this._descriptionForCommand("workbench.action.terminal.accessibleBufferGoToNextCommand" , ( localizeWithPath(_moduleId, 6, 'Go to Next Command ({0}) in the accessible view')), ( localizeWithPath(
                _moduleId,
                7,
                'Go to Next Command in the accessible view is currently not triggerable by a keybinding.'
            ))));
            shellIntegrationCommandList.push('- ' + this._descriptionForCommand("workbench.action.terminal.accessibleBufferGoToPreviousCommand" , ( localizeWithPath(_moduleId, 8, 'Go to Previous Command ({0}) in the accessible view')), ( localizeWithPath(
                _moduleId,
                9,
                'Go to Previous Command in the accessible view is currently not triggerable by a keybinding.'
            ))));
            shellIntegrationCommandList.push('- ' + this._descriptionForCommand("editor.action.accessibleViewGoToSymbol" , ( localizeWithPath(_moduleId, 10, 'Go to Symbol ({0})')), ( localizeWithPath(
                _moduleId,
                11,
                'Go to symbol is currently not triggerable by a keybinding.'
            ))));
            shellIntegrationCommandList.push('- ' + this._descriptionForCommand("workbench.action.terminal.runRecentCommand" , ( localizeWithPath(_moduleId, 12, 'Run Recent Command ({0})')), ( localizeWithPath(
                _moduleId,
                13,
                'Run Recent Command is currently not triggerable by a keybinding.'
            ))));
            shellIntegrationCommandList.push('- ' + this._descriptionForCommand("workbench.action.terminal.goToRecentDirectory" , ( localizeWithPath(_moduleId, 14, 'Go to Recent Directory ({0})')), ( localizeWithPath(
                _moduleId,
                15,
                'Go to Recent Directory is currently not triggerable by a keybinding.'
            ))));
            content.push(shellIntegrationCommandList.join('\n'));
        }
        else {
            content.push(this._descriptionForCommand("workbench.action.terminal.runRecentCommand" , ( localizeWithPath(
                _moduleId,
                16,
                'The Go to Recent Directory command ({0}) enables screen readers to easily navigate to a directory that has been used in the terminal.'
            )), ( localizeWithPath(
                _moduleId,
                17,
                'The Go to Recent Directory command enables screen readers to easily navigate to a directory that has been used in the terminal and is currently not triggerable by a keybinding.'
            ))));
        }
        content.push(this._descriptionForCommand("workbench.action.terminal.openDetectedLink" , ( localizeWithPath(
            _moduleId,
            18,
            'The Open Detected Link ({0}) command enables screen readers to easily open links found in the terminal.'
        )), ( localizeWithPath(
            _moduleId,
            19,
            'The Open Detected Link command enables screen readers to easily open links found in the terminal and is currently not triggerable by a keybinding.'
        ))));
        content.push(this._descriptionForCommand("workbench.action.terminal.newWithProfile" , ( localizeWithPath(
            _moduleId,
            20,
            'The Create New Terminal (With Profile) ({0}) command allows for easy terminal creation using a specific profile.'
        )), ( localizeWithPath(
            _moduleId,
            21,
            'The Create New Terminal (With Profile) command allows for easy terminal creation using a specific profile and is currently not triggerable by a keybinding.'
        ))));
        content.push(( localizeWithPath(
            _moduleId,
            22,
            'Configure what gets focused after running selected text in the terminal with `{0}`.',
            "terminal.integrated.focusAfterRun"
        )));
        return content.join('\n\n');
    }
};
TerminalAccessibilityHelpProvider = ( (__decorate([
    ( (__param(2, IInstantiationService))),
    ( (__param(3, IKeybindingService))),
    ( (__param(4, IContextKeyService))),
    ( (__param(5, ICommandService))),
    ( (__param(6, IAccessibilityService))),
    ( (__param(7, IConfigurationService)))
], TerminalAccessibilityHelpProvider)));

export { TerminalAccessibilityHelpProvider };
